import { Component, Input, Output, EventEmitter, Optional, Self, HostListener, ViewChild, ElementRef, AfterViewChecked, inject, ContentChild, TemplateRef, OnInit, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NgControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslatePipe, TranslateService } from '@dental-ui/shared/i18n';
import { DropdownService } from './dropdown.service';
import { Subject, takeUntil } from 'rxjs';

export interface DropdownOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

@Component({
  selector: 'lib-dropdown',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslatePipe],
  templateUrl: './dropdown.component.html',
  styleUrl: './dropdown.component.scss',
})
export class DropdownComponent implements ControlValueAccessor, AfterViewChecked, OnInit, OnDestroy {
  @ContentChild('optionTemplate') optionTemplate: TemplateRef<{ $implicit: DropdownOption }> | null = null;
  @ContentChild('triggerTemplate') triggerTemplate: TemplateRef<{ $implicit: DropdownOption }> | null = null;

  private translateService = inject(TranslateService);
  private dropdownService = inject(DropdownService);
  private destroy$ = new Subject<void>();

  getSelectedOption(): DropdownOption | undefined {
    return this.options.find(o => o.value === this.selectedValue);
  }
  @Input() label = '';
  @Input() placeholder = 'Select an option';
  @Input() options: DropdownOption[] = [];
  @Input() disabled = false;
  @Input() required = false;
  @Input() errorMessage = '';
  @Input() hint = '';
  @Input() searchable = false;
  @Input() searchPlaceholder = 'Search...';
  @Input() showSelectOption = true;
  @Input() isLoading = false;
  @Input() loadingText = 'Loading...';
  @Input() noResultsText = 'No results found';

  @Output() selectionChange = new EventEmitter<string | number>();
  @Output() actionButtonClick = new EventEmitter<void>();
  @Output() searchChange = new EventEmitter<string>();

  @ViewChild('searchInput', { static: false }) searchInput?: ElementRef<HTMLInputElement>;

  isOpen = false;
  selectedValue: string | number | null = null;
  searchTerm = '';
  dropdownId = `dropdown-${Math.random().toString(36).substr(2, 9)}`;
  private shouldFocusSearch = false;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onChange = (_value: string | number | null) => {
    // ControlValueAccessor interface requirement
  };
  onTouched = () => {
    // ControlValueAccessor interface requirement
  };

  // ControlValueAccessor pattern requires NgControl injection via constructor
  // This is the standard Angular pattern for custom form controls
  // eslint-disable-next-line
  constructor(@Optional() @Self() public ngControl: NgControl | null) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    this.dropdownService.openDropdown$
      .pipe(takeUntil(this.destroy$))
      .subscribe((id) => {
        if (id !== this.dropdownId && this.isOpen) {
          this.isOpen = false;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }


  get selectedLabel(): string {
    if (this.selectedValue === null || this.selectedValue === undefined || this.selectedValue === '') {
      return this.placeholder;
    }
    const option = this.options.find(opt => opt.value === this.selectedValue);
    return option ? option.label : this.placeholder;
  }

  get filteredOptions(): DropdownOption[] {
    if (!this.searchable || !this.searchTerm.trim()) {
      return this.options;
    }
    const search = this.normalizeSearch(this.searchTerm);
    return this.options.filter(opt => {
      const translatedLabel = this.normalizeSearch(this.translateService.getText(opt.label));
      const originalLabel = this.normalizeSearch(opt.label);
      return translatedLabel.includes(search) || originalLabel.includes(search);
    });
  }

  private normalizeSearch(val: string): string {
    if (!val) return '';
    
    // Turkish specific handling: İ -> i, I -> ı
    // We also handle common accents used in Turkish (ö, ü, ç, ş, ğ)
    // for a more "forgiving" search if the user types without those characters.
    return val
      .replace(/İ/g, 'i')
      .replace(/I/g, 'ı')
      .toLocaleLowerCase('tr-TR')
      .trim();
  }

  get hasError(): boolean {
    if (this.errorMessage) {
      return true;
    }
    if (this.ngControl?.errors && this.ngControl?.touched) {
      return true;
    }
    return false;
  }

  get errorText(): string {
    if (this.errorMessage) {
      return this.errorMessage;
    }
    if (this.ngControl?.errors && this.ngControl?.touched) {
      const errors = this.ngControl.errors;
      if (errors['required']) {
        return 'This field is required';
      }
      return 'Invalid selection';
    }
    return '';
  }

  focusedIndex = -1;

  @ViewChild('ddTrigger') ddTrigger?: ElementRef<HTMLButtonElement>;
  isDropup = false;

  toggleDropdown(): void {
    if (!this.disabled) {
      if (!this.isOpen) {
        this.checkDirection();
        this.isOpen = true;
        this.dropdownService.notifyOpen(this.dropdownId);
        this.onTouched();
        this.focusedIndex = -1;
        if (this.searchable) {
          this.shouldFocusSearch = true;
        }
      } else {
        this.isOpen = false;
        this.searchTerm = '';
        this.isDropup = false;
      }
    }
  }

  private getEstimatedPanelHeight(): number {
    const searchHeight = this.searchable ? 52 : 0;
    const optionsHeight = 160;
    const panelGap = 4;
    return searchHeight + optionsHeight + panelGap;
  }

  private checkDirection(): void {
    if (!this.ddTrigger) return;

    const rect = this.ddTrigger.nativeElement.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const panelHeight = this.getEstimatedPanelHeight();

    // Yukarı aç: altta yeterli yer yoksa ve üstte daha fazla alan varsa
    this.isDropup = spaceBelow < panelHeight && spaceAbove > spaceBelow;
  }

  ngAfterViewChecked(): void {
    if (this.shouldFocusSearch && this.searchInput) {
      this.searchInput.nativeElement.focus();
      this.shouldFocusSearch = false;
    }
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (this.disabled) return;

    const options = this.filteredOptions;
    const totalCount = this.showSelectOption ? options.length + 1 : options.length;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!this.isOpen) {
          this.toggleDropdown();
        } else {
          this.focusedIndex = (this.focusedIndex + 1) % totalCount;
          this.scrollToFocused();
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (this.isOpen) {
          this.focusedIndex = (this.focusedIndex - 1 + totalCount) % totalCount;
          this.scrollToFocused();
        }
        break;
      case 'Enter':
        if (this.isOpen && this.focusedIndex >= 0) {
          event.preventDefault();
          if (this.showSelectOption) {
            if (this.focusedIndex === 0) {
              this.selectOption(null);
            } else {
              this.selectOption(options[this.focusedIndex - 1]);
            }
          } else {
            this.selectOption(options[this.focusedIndex]);
          }
        }
        break;
      case 'Escape':
        if (this.isOpen) {
          event.preventDefault();
          this.isOpen = false;
        }
        break;
      case 'Tab':
        this.isOpen = false;
        break;
    }
  }

  private scrollToFocused(): void {
    // We'll use a simple approach here, in a real premium component 
    // we'd use ViewChildren to find the specific element and scroll into view.
    // For now, this state tracking is enough for the UI to highlight the item.
  }

  onSearchChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchTerm = input.value;
    this.focusedIndex = -1; // Reset focus on search
    this.searchChange.emit(this.searchTerm);
  }

  selectOption(option: DropdownOption | null): void {
    if (option && option.disabled) {
      return;
    }
    this.selectedValue = option ? option.value : null;
    this.isOpen = false;
    this.searchTerm = '';
    this.focusedIndex = -1;
    this.onChange(this.selectedValue);
    if (option) {
      this.selectionChange.emit(option.value);
    } else {
      this.selectionChange.emit(null as unknown as string | number);
    }
  }

  onActionButtonClick(): void {
    this.actionButtonClick.emit();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.dropdown-wrapper')) {
      this.isOpen = false;
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: string | number | null): void {
    this.selectedValue = value;
  }

  registerOnChange(fn: (value: string | number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.isOpen = false;
    }
  }
}

